<template>
  <div v-show="displayFrontend" :class="{questions__save: safeFreeze}" class="questions">
    <h1 class="questions__header">{{ $t("Break rules") }}</h1>
    <p>{{ $t("Please fill out your break policy below as specifically as possible. These are the unpaid breaks.") }}</p>
    <p>{{
        $t('There can be rules that relate to the length of a shift (after 5.5 hours 30 minutes break and after 8 hours 60 min break in total), or to the time at which work is done (if working at 12{colon}00 pm then 30 minutes break). If you do not want to calculate the break automatically, it can also be indicated via the schedule per shift. These settings can be adjusted later in Dyflexis.', {colon: ':'})
      }}</p>

    <b-form-checkbox
      v-model="showLengthBreak" class="checkbox" name="check-button" switch>
      {{ $t("Allocate break based on length of shift") }}
    </b-form-checkbox>

    <b-form-checkbox
      v-model="showTimeBreak" class="checkbox" name="check-button" switch>
      {{ $t("Allocate break based on time of day") }}
    </b-form-checkbox>

    <div v-show="showLengthBreak">
      <h2 class="questions__subheader questions__header">{{ $t("Allocate break based on length of shift") }}</h2>

      <p>{{ $t("If person worked x hours, then y minutes break (in total)") }}</p>
      <div v-for="timeItem in breakSettings.workedHoursBreak" class="breakItem">
        <p>
          {{ $t("In case") }} <input
          v-model="timeItem.hoursWorked"
          :disabled="readOnly"
          class="questions--minutesBreak form-control"
          min="0"
          type="number"/> {{ $t("hours worked") }}
          <input
            v-model="timeItem.breakLength"
            :disabled="readOnly"
            class="form-control questions--minutesBreak"
            min="1"
            type="number"/> {{ $t("minutes break") }}.
          {{ $t("Name") }}
          <input
            v-model="timeItem.group"
            :disabled="readOnly"
            class="form-control inline max8vw"
            type="text"
            @change="sortWorkedHoursBreak"/>
        </p>
      </div>

      <b-button
        variant="success"
        @click="addRowWorkedHours">
        +
      </b-button>
    </div>

    <div v-show="showTimeBreak">
      <h2 class="questions__subheader questions__header">{{ $t("Allocate break based on time of day") }}</h2>
      <p>{{
          $t('You set 10{colon}15 is 10 minutes of break. If someone is working this time, 10 minutes of break time is added. The final break time is the sum of all the times you define here. For fairness, employee must have worked half an hour (or have half an hour left to work).', {colon: ':'})
        }}</p>

      <div v-for="timeItem in breakSettings.timeBreak" class="breakItem">
        <p>
          {{ $t("If working at") }}
          <b-form-input
            v-model="timeItem.breakTime"
            :disabled="readOnly"
            class="questions--smallinput questions--inputmargin max7vw inline"
            type="time">
          </b-form-input>

          {{ $t("allocate") }}
          <input
            v-model="timeItem.breakLength"
            :disabled="readOnly"
            class="form-control questions--minutesBreak inline"
            type="number"/>
          {{ $t("minutes of break time. Name:") }}

          <b-form-input
            v-model="timeItem.group"
            :disabled="readOnly"
            class="max8vw inline"
            type="text"
            @change="sortWorkedHoursBreak">
          </b-form-input>
        </p>
      </div>

      <b-button
        variant="success"
        @click="addRowTimeBreak">
        +
      </b-button>
    </div>

    <p><br><i>{{ $t("If you have multiple break policies, which one is the default? Explain below.") }}</i></p>
    <h2 class="questions__subheader">{{ $t("Remarks") }}</h2>
    <div>
      <b-form-textarea
        v-model="breakSettings.breakRulesRemarks"
        class="questions__textarea"
        rows="3"/>
    </div>

    <input :value="$t('Save')" type="button" @click="handleSave"/>
    <p v-show="showSaveStatus" class="questions--savesuccess">{{ $t("Saved successfully!") }}</p>
  </div>
</template>

<script>
import SystemRequestService from "@/SystemRequestService";
import GlobalFunctions from "@/GlobalFunctions";

export default {
  name: "BreakSettings",
  data() {
    return {
      displayFrontend: false,
      readOnly: false,
      safeFreeze: false,
      showSaveStatus: false,
      showTimeBreak: false,
      showLengthBreak: false,
      highestGroupCount: 0,
      breakSettings: {
        workedHoursBreak: [{
          hoursWorked: null,
          breakLength: null,
          group: null,
        }],
        timeBreak: [{
          breakTime: null,
          breakLength: null,
          group: null,
        }],
        breakRulesRemarks: null,
      }
    };
  },
  async beforeMount() {
    const result = await SystemRequestService.getProject(this.$route.params.hashedId);
    if (!result) {
      alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
      return;
    }

    if (result.breakRules !== null && result.breakRules !== '[]') {
      let breakRules;
      try {
        breakRules = JSON.parse(this.removeHtml(result.breakRules.replaceAll("&quot;", '"')));
      } catch (e) {
        this.displayFrontend = true;
        return false;
      }

      this.breakSettings = breakRules;
    } else {
      await this.change();
    }

    if (this.breakSettings.workedHoursBreak !== undefined) {
      this.breakSettings.workedHoursBreak.forEach(function getHighest(entry) {
        if (entry.group === undefined) {
          entry.group = null;
        }
      });
    }

    if (this.breakSettings.timeBreak !== undefined) {
      this.breakSettings.timeBreak.forEach(function getHighest(entry) {
        if (entry.group === undefined) {
          entry.group = null;
        }
      });
    }

    this.breakSettings.breakRulesRemarks = result.breakRulesRemarks;
    if (this.breakSettings.timeBreak && this.breakSettings.timeBreak[0].breakTime !== null) {
      this.showTimeBreak = true;
    }

    if (this.breakSettings.workedHoursBreak && this.breakSettings.workedHoursBreak[0].hoursWorked !== null) {
      this.showLengthBreak = true;
    }

    this.sortWorkedHoursBreak();

    this.readOnly = result.isSigned;
    GlobalFunctions.setReadOnly(result.isSigned);
    localStorage.setItem('startingObject', JSON.stringify(this.breakSettings));
    this.displayFrontend = true;
  },
  beforeRouteLeave: function (to, from, next) {
    if (!GlobalFunctions.acceptPageLeave(this.breakSettings, this.$t("You have made changes without saving them. Press cancel to ignore these changes and leave the page."))) {
      this.handleSave(false);
    }
    next();
  },
  methods: {
    addRowWorkedHours() {
      if (this.breakSettings.workedHoursBreak === null) {
        this.breakSettings.workedHoursBreak = [];
      }

      this.breakSettings.workedHoursBreak.push({
        hoursWorked: null,
        breakLength: null,
      });
      this.$forceUpdate();
    },
    addRowTimeBreak() {
      if (!this.breakSettings.timeBreak) {
        this.breakSettings.timeBreak = [];
      }

      this.breakSettings.timeBreak.push({
        breakTime: null,
        breakLength: null,
      });
      this.$forceUpdate();
    },
    change() {
      this.breakSettings.timeBreak = null;
      this.breakSettings.workedHoursBreak = null

      this.addRowTimeBreak();
      this.addRowWorkedHours();
    },
    async handleSave(saveInitialObject) {
      this.safeFreeze = true;
      const result = await SystemRequestService.updateProject(this.breakSettings, this.$route.params.hashedId);
      if (!result || result.status !== 'success') {
        alert(this.$t("Error while updating, please send a screenshot of the fields on this page to your Dyflexis consultant!"));
        return;
      }

      if (saveInitialObject !== false) {
        localStorage.setItem('startingObject', JSON.stringify(this.breakSettings));
      }

      this.safeFreeze = false;
      this.showSaveStatus = true;
      setTimeout(() => {
        this.showSaveStatus = false;
      }, 3000);
    },
    removeHtml(text) {
      if (text == null) {
        return text;
      }

      let tmp = document.createElement("DIV");
      tmp.innerHTML = text;
      return tmp.textContent || tmp.innerText || "";
    },
    sortWorkedHoursBreak() {
      if (this.breakSettings.workedHoursBreak !== null) {
        this.breakSettings.workedHoursBreak.sort(function (a, b) {
          if (a.group == null || b.group == null) {
            return 0;
          }

          if (a.group < b.group) return -1;
          if (a.group > b.group) return 1;
          return 0;
        });
      }

      if (this.breakSettings.timeBreak !== undefined) {
        this.breakSettings.timeBreak.sort(function (a, b) {
          if (a.group == null || b.group == null) {
            return 0;
          }

          if (a.group < b.group) return -1;
          if (a.group > b.group) return 1;
          return 0;
        });
      }
    }
  },
};
</script>
